import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { empMediaLinksService } from '@/services/empMediaLinksService'
import { employeesService } from '@/services/employeesService'
import type { MediaLinksVm } from '@/api/types/empMediaLinks.types'
import { showError, showSuccess } from '@/lib/swal'

export const MediaLinksEdit: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId
  const isEditing = Boolean(id && id !== 'new')

  const [loading, setLoading] = useState(false)
  const [employees, setEmployees] = useState<{ id?: number; empCode?: string; empFirstNameEn?: string; empSecondNameEn?: string; empLastNameEn?: string }[]>([])

  const [form, setForm] = useState({
    empId: 0,
    twitterLink: '',
    linkedinLink: '',
    instagramLink: '',
    facebookLink: '',
    websiteLink: '',
    otherLink: '',
  })

  useEffect(() => {
    if (!branchId) return
    const loadEmployees = async () => {
      try {
        const res = await employeesService.getAllEmployees(branchId, 1, 500)
        const data = (res as any)?.data ?? (res as any)?.Data ?? res
        setEmployees(Array.isArray(data) ? data : [])
      } catch {
        setEmployees([])
      }
    }
    loadEmployees()
  }, [branchId])

  useEffect(() => {
    if (!isEditing || !id) return
    const load = async () => {
      try {
        setLoading(true)
        const data = await empMediaLinksService.getById(Number(id))
        const raw = data as any
        setForm({
          empId: raw.empId ?? raw.employeeId ?? 0,
          twitterLink: raw.twitterLink ?? '',
          linkedinLink: raw.linkedInLink ?? raw.linkedinLink ?? '',
          instagramLink: raw.instagramLink ?? '',
          facebookLink: raw.facebookLink ?? '',
          websiteLink: raw.websiteLink ?? '',
          otherLink: raw.otherLink ?? '',
        })
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? 'Failed to load record.')
        navigate('/media-links')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isEditing, id, navigate])

  const employeeOptions = useMemo(
    () =>
      employees.map((e) => {
        const fullName = [e.empFirstNameEn, e.empSecondNameEn, e.empLastNameEn].filter(Boolean).join(' ').trim()
        const label = fullName ? `${e.empCode ?? ''} — ${fullName}`.trim() : e.empCode ?? `Emp ${e.id}`
        return { value: String(e.id ?? 0), label }
      }).filter((o) => o.value !== '0'),
    [employees]
  )

  const handleChange = (patch: Partial<typeof form>) => setForm((prev) => ({ ...prev, ...patch }))

  const handleSave = async () => {
    if (!branchId) {
      toast.error('Please select a branch.')
      return
    }
    if (!form.empId || form.empId === 0) {
      toast.error('Please select an employee.')
      return
    }
    if (!form.twitterLink.trim()) {
      toast.error('Twitter Link is required.')
      return
    }
    if (!form.linkedinLink.trim()) {
      toast.error('Linkedin Link is required.')
      return
    }
    if (!form.instagramLink.trim()) {
      toast.error('Instagram Link is required.')
      return
    }
    if (!form.facebookLink.trim()) {
      toast.error('Facebook Link is required.')
      return
    }
    if (!form.websiteLink.trim()) {
      toast.error('Website Link is required.')
      return
    }
    if (!form.otherLink.trim()) {
      toast.error('Other Link is required.')
      return
    }

    const payload: MediaLinksVm = {
      id: isEditing && id ? Number(id) : 0,
      // Backend expects `empMediaLink` as required identifier
      empMediaLink: isEditing && id ? Number(id) : 0,
      empId: form.empId,
      twitterLink: form.twitterLink.trim(),
      linkedinLink: form.linkedinLink.trim(),
      instagramLink: form.instagramLink.trim(),
      facebookLink: form.facebookLink.trim(),
      websiteLink: form.websiteLink.trim(),
      // Backend validation says `otherLink` is a single char, so send 1 character.
      otherLink: form.otherLink.trim().charAt(0),
      branchId,
    }

    try {
      setLoading(true)
      if (isEditing) {
        await empMediaLinksService.update(payload)
        await showSuccess('Updated', 'Social media links updated successfully.')
      } else {
        await empMediaLinksService.save(payload)
        await showSuccess('Saved', 'Social media links saved successfully.')
      }
      navigate('/media-links')
    } catch (e: any) {
      await showError('Save failed', e?.response?.data?.message ?? e?.message ?? 'Failed to save.')
    } finally {
      setLoading(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title="Social Media Links" description="Add or edit social media links." />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">Please select a branch first.</CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Social Media Links"
        description={isEditing ? 'Edit social media links' : 'Add new social media links'}
      />
      <Card>
        <CardHeader>
          <CardTitle>Social Media Links</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <SearchableSelect
                options={[{ value: '0', label: 'Select employee' }, ...employeeOptions]}
                value={form.empId ? String(form.empId) : '0'}
                onValueChange={(v) => handleChange({ empId: Number(v) || 0 })}
                placeholder="Select employee"
                searchPlaceholder="Search employee..."
                emptyMessage="No employee found"
                disabled={isEditing}
              />
            </div>
            <div className="space-y-2">
              <Label>Twitter Link *</Label>
              <Input value={form.twitterLink} onChange={(e) => handleChange({ twitterLink: e.target.value })} placeholder="TwitterLink" />
            </div>
            <div className="space-y-2">
              <Label>Linkedin Link *</Label>
              <Input value={form.linkedinLink} onChange={(e) => handleChange({ linkedinLink: e.target.value })} placeholder="LinkedinLink" />
            </div>
            <div className="space-y-2">
              <Label>Facebook Link *</Label>
              <Input value={form.facebookLink} onChange={(e) => handleChange({ facebookLink: e.target.value })} placeholder="FacebookLink" />
            </div>
            <div className="space-y-2">
              <Label>Instagram Link *</Label>
              <Input value={form.instagramLink} onChange={(e) => handleChange({ instagramLink: e.target.value })} placeholder="InstagramLink" />
            </div>
            <div className="space-y-2">
              <Label>Website Link *</Label>
              <Input value={form.websiteLink} onChange={(e) => handleChange({ websiteLink: e.target.value })} placeholder="WebsiteLink" />
            </div>
            <div className="space-y-2">
              <Label>Other Link *</Label>
              <Input value={form.otherLink} onChange={(e) => handleChange({ otherLink: e.target.value })} placeholder="OtherLink" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button type="button" variant="outline" onClick={() => navigate('/media-links')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Return
            </Button>
            <Button type="button" onClick={handleSave} disabled={loading}>
              <Save className="w-4 h-4 mr-2" /> Save
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
